<template>
  <div class="sidebar">
    <focus-trap :active="open">
      <nav id="sidebarMenu"
           tabindex="-1"
           class="sidebar-menu"
           ref="sidebarMenu"
           :aria-hidden="!open"
           aria-labelledby="sidebarMenuButton"
           role="menu"
           :class="{'open': open, 'ios-pwa': isIosPwa}">
        <div id="sidebarMenuParent">
          <b-row no-gutters>
            <b-col>
              <button id="sidebar-close-button"
                      class="btn button--icon"
                      type="button"
                      role="menuitem"
                      aria-label="Close Menu"
                      @click="closeSidebar">
                <inline-svg src="XCloseIcon"
                            class="close-button icon--brand">
                </inline-svg>
              </button>
            </b-col>
          </b-row>
          <div>
            <cash-1-loans-sidebar v-if="showCash1LoansSidebar"></cash-1-loans-sidebar>
            <california-check-cash-sidebar v-if="showCaliforniaCheckCashSidebar"></california-check-cash-sidebar>
            <check-into-cash-sidebar v-if="showCheckIntoCashSidebar"></check-into-cash-sidebar>
            <check-smart-sidebar v-if="showCheckSmartSidebar"></check-smart-sidebar>
            <community-choice-sidebar v-if="showCommunityChoiceSidebar"></community-choice-sidebar>
            <easy-money-sidebar v-if="showEasyMoneySidebar"></easy-money-sidebar>
            <debug-actions></debug-actions>
            <instaloan-sidebar v-if="showInstaLoanSidebar"></instaloan-sidebar>
            <referral-sidebar v-if="showReferralSidebar"></referral-sidebar>
            <titlebucks-sidebar v-if="showTitleBucksSidebar"></titlebucks-sidebar>
            <titlemax-sidebar v-if="showTitlemaxSidebar"></titlemax-sidebar>
          </div>

          <div id="sidebarMenuButtonRow">
            <b-row v-if="showApplyForLoan">
              <b-col cols="12">
                <button class="btn button--primary"
                        type="button"
                        @click="goToApplyNow">
                  {{ $t('global.apply_for_loan') }}
                </button>
              </b-col>
            </b-row>

            <b-row class="spacing-3">
              <b-col cols="12">
                <button v-if="showSignOut"
                        class="btn button--outline"
                        type="button"
                        @click="logout">
                  {{ $t('global.sign_out') }}
                </button>
                <button v-else
                        class="btn button--secondary"
                        type="button"
                        @click="goAppPage('Login')">
                  {{ $t('global.sign_in') }}
                </button>
              </b-col>
            </b-row>
          </div>
        </div>
      </nav>
    </focus-trap>
  </div>
</template>

<script>
// Elements
import Cash1LoansSidebar from '../../loans/elements/c1l/Cash1LoansSidebar.vue'
import CaliforniaCheckCashSidebar from '../../loans/elements/ccc/CaliforniaCheckCashSidebar.vue'
import CheckIntoCashSidebar from '../../loans/elements/cic/CheckIntoCashSidebar.vue'
import CheckSmartSidebar from '../../loans/elements/chs/CheckSmartSidebar.vue'
import CommunityChoiceSidebar from '../../loans/elements/ccf/CommunityChoiceSidebar.vue'
import DebugActions from './DebugActions.vue'
import EasyMoneySidebar from '../../loans/elements/esm/EasyMoneySidebar.vue'
import InlineSvg from './InlineSvg.vue'
import InstaloanSidebar from '../../loans/elements/inl/InstaloanSidebar.vue'
import ReferralSidebar from '../../referral/elements/ReferralSidebar.vue'
import TitlebucksSidebar from '../../loans/elements/tbs/TitlebucksSidebar.vue'
import TitlemaxSidebar from '../../loans/elements/tmx/TitlemaxSidebar.vue'

// Libraries
import { FocusTrap } from 'focus-trap-vue'
import { mapGetters, mapState } from 'vuex'

// Utilities
import logger from '../../../utility/loggerUtil'
import router from '../../../utility/routerUtil'
import analytics from '../../../utility/analyticsUtil'

// Constants
import { AnalyticsConstants, BrandEnum, BrowserType } from '../../../constants.js'

// Mixins
import { sidebarMixin } from '../../../mixins/sidebarMixin'

export default {
  name: 'sidebar',

  components: {
    Cash1LoansSidebar,
    CaliforniaCheckCashSidebar,
    CheckIntoCashSidebar,
    CheckSmartSidebar,
    CommunityChoiceSidebar,
    DebugActions,
    EasyMoneySidebar,
    FocusTrap,
    InlineSvg,
    InstaloanSidebar,
    TitlebucksSidebar,
    TitlemaxSidebar,
    ReferralSidebar
  },

  mixins: [sidebarMixin],

  data () {
    return {
      serviceCallError: ''
    }
  },

  computed: {
    open () {
      return this.sidebarOpen
    },

    showInstaLoanSidebar () {
      return this.open && router.getCurrentRouteBrandEnum(this.$route) === BrandEnum.InstaLoan && !this.showReferralSidebar
    },

    showTitleBucksSidebar () {
      return this.open && router.getCurrentRouteBrandEnum(this.$route) === BrandEnum.TitleBucks && !this.showReferralSidebar
    },

    showTitlemaxSidebar () {
      return this.open && router.getCurrentRouteBrandEnum(this.$route) === BrandEnum.TitleMax && !this.showReferralSidebar
    },

    showCash1LoansSidebar () {
      return this.open && router.getCurrentRouteBrandEnum(this.$route) === BrandEnum.Cash1Loans && !this.showReferralSidebar
    },

    showCaliforniaCheckCashSidebar () {
      return this.open && router.getCurrentRouteBrandEnum(this.$route) === BrandEnum.CaliforniaCheckCash && !this.showReferralSidebar
    },

    showCheckIntoCashSidebar () {
      return this.open && router.getCurrentRouteBrandEnum(this.$route) === BrandEnum.CheckIntoCash && !this.showReferralSidebar
    },

    showCheckSmartSidebar () {
      return this.open && router.getCurrentRouteBrandEnum(this.$route) === BrandEnum.CheckSmart && !this.showReferralSidebar
    },

    showCommunityChoiceSidebar () {
      return this.open && router.getCurrentRouteBrandEnum(this.$route) === BrandEnum.CommunityChoice && !this.showReferralSidebar
    },

    showEasyMoneySidebar () {
      return this.open && router.getCurrentRouteBrandEnum(this.$route) === BrandEnum.EasyMoney && !this.showReferralSidebar
    },

    showReferralSidebar () {
      return this.isNonCustomer && this.isReferralRewardsUser && this.authenticated
    },

    isIosPwa () {
      return this.browserType === BrowserType.Safari && this.isPwa
    },

    showApplyForLoan () {
      return (router.getCurrentRouteBrandEnum(this.$route) === BrandEnum.TitleMax ||
              router.getCurrentRouteBrandEnum(this.$route) === BrandEnum.InstaLoan) &&
              !this.showReferralSidebar
    },

    showSignOut () {
      return this.authenticated
    },

    ...mapGetters('referral', ['isReferralRewardsUser']),
    ...mapGetters('customer', ['isNonCustomer']),
    ...mapState('display', ['browserType', 'isPwa', 'sidebarOpen']),
    ...mapState('session', ['authenticated'])
  },

  methods: {
    closeSidebar () {
      this.$store.commit('display/closeSidebar')
    },

    async goToApplyNow () {
      if (router.getCurrentRouteBrandEnum(this.$route) === BrandEnum.InstaLoan) {
        await this.goExternalPage(process.env.VUE_APP_InstaLoanApplyNowSiteUrl, 'InstaLoan Apply For Loan')
      } else if (router.getCurrentRouteBrandEnum(this.$route) === BrandEnum.TitleBucks) {
        await this.goExternalPage(process.env.VUE_APP_TbsApplyNowSiteUrl, 'TitleBucks Apply For Loan')
      } else {
        await this.goExternalPage(process.env.VUE_APP_TmxApplyNowSiteUrl, 'Apply For Loan')
      }
    },

    async logout () {
      try {
        await this.$store.dispatch('session/signout')

        analytics.logEvent(AnalyticsConstants.eventName.logout, { method: AnalyticsConstants.method.logout.sideBarButton }, { logMarketingEvent: true })

        this.$router.push({ name: 'Login' })
      } catch (e) {
        logger.error('Error attempting to logout and navigate to login page')
        logger.debug(e)
      }
    }
  },

  watch: {
    open (open) {
      if (open) {
        try {
          document.getElementById('sidebarMenu').focus()
        } catch (e) {
          logger.error('Unable to focus')
          logger.debug(e)
        }
      }
    }
  }
}
</script>

<style lang="scss">
@use '../../../assets/styles/colors';

.sidebar {
  background-color: colors.$white;
}

.sidebar-menu {
  width: 0;
  height: 100vh;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  box-shadow: 0 2px 10px 0 colors.$black-25op;
  transition: width 0.25s ease;
}

.sidebar-menu.ios-pwa {
  padding-top: 1.25rem; /* Adding padding for iOS status bar */
}

#sidebarMenuParent {
  min-height: calc(100% - calc(constant(safe-area-inset-bottom) + 42px)); /* iOS 11.0 */
  min-height: calc(100% - calc(env(safe-area-inset-bottom) + 42px)); /* iOS 11.2 */
}

#sidebarMenuButtonRow {
  padding: 1rem 1rem 1.25rem 1rem;
}

.sidebar-menu.open {
  width: 18.62rem;
}

.sidebar-menu #sidebar-close-button,
.sidebar-menu .menu-row{
  padding-left: constant(safe-area-inset-left); /* iOS 11.0 */
  padding-left: env(safe-area-inset-left); /* iOS 11.2 */
}

.sidebar-menu .close-button {
  width: 2.5rem;
  height: 2.5rem;
  margin-top: .75rem;
  margin-left: .9rem;
  cursor: pointer;
}
</style>
